import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import { format } from 'date-fns';
import type { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { OverlappingAvatars } from '@sorare/core/src/components/rivals/OverlappingAvatars';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { groupBy, sortBy } from '@sorare/core/src/lib/arrays';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Game } from 'components/game/Game';
import { HorizontalGame } from 'components/game/HorizontalGame';
import { WithFixtureHeaderLayout } from 'layouts/WithFixtureHeaderLayout';

import type {
  CompetitionGamesPage_so5LeaderboardGroupInterface,
  CompetitionGamesPages_paginatedSo5LeaderboardGroupInterface,
} from './__generated__/index.graphql';

const Content = styled.div`
  padding: var(--double-unit) 0;
  isolation: isolate;
`;
const DayContainer = styled(Horizontal)`
  position: sticky;
  top: calc(16 * var(--unit));
  z-index: 1;

  @media ${tabletAndAbove} {
    top: calc(10 * var(--unit));
  }
`;
const GameWrapper = styled.div`
  border-radius: var(--double-unit);
  background: linear-gradient(var(--color), rgba(33, 33, 33, 1));
  background-origin: border-box;
`;
const FilterItem = styled(ButtonBase)`
  padding: var(--half-unit) var(--intermediate-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-200);
  &.selected {
    background-color: var(--c-white);
    color: var(--c-black);
  }
`;

type Props = {
  so5LeaderboardGroupInterface: CompetitionGamesPage_so5LeaderboardGroupInterface;
  fixture: string;
  competition: string;
  paginated: {
    paginatedSo5LeaderboardGroupInterface?: CompetitionGamesPages_paginatedSo5LeaderboardGroupInterface;
    loading: boolean;
    loadMore: (cursor?: string | null) => void;
  };
  showGamesWithoutCards: boolean;
  setShowGamesWithoutCards: (b: boolean) => void;
};

export const CompetitionGamesPage = ({
  so5LeaderboardGroupInterface,
  competition,
  paginated: { paginatedSo5LeaderboardGroupInterface, loading, loadMore },
  setShowGamesWithoutCards,
  showGamesWithoutCards,
}: Props) => {
  const openDialog = useOpenDialog();
  const isLaptopAndAbove = useIsLaptop();

  const { generateSportContextPath } = useSportContext();

  const { gamesWithMyCardsPreview } =
    paginatedSo5LeaderboardGroupInterface?.so5LeagueTrack
      ?.entrySo5Leaderboard || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(gamesWithMyCardsPreview?.pageInfo.endCursor);
    },
    !!gamesWithMyCardsPreview?.pageInfo.hasNextPage,
    loading
  );

  if (!so5LeaderboardGroupInterface) {
    return null;
  }

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(
      ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION,
      {
        params: {
          eventType: so5LeaderboardGroupInterface.so5Fixture.type.toLowerCase(),
          fixture: so5LeaderboardGroupInterface.so5Fixture.slug,
          competition,
        },
      }
    ),
    -1
  );

  const sortedGamesWithCardsPreview = sortBy(
    gameWithCardsPreview => gameWithCardsPreview.game.date,
    [...(gamesWithMyCardsPreview?.nodes || [])]
  );

  const sortedDays = [
    ...new Set(
      sortedGamesWithCardsPreview.map(({ game }) =>
        format(game.date, 'yyyy-MM-dd')
      )
    ),
  ];

  const gamesWithCardsPreviewByDay = groupBy(
    sortedGamesWithCardsPreview,
    ({ game }) => format(game.date, 'yyyy-MM-dd')
  );

  return (
    <WithFixtureHeaderLayout
      so5Fixture={so5LeaderboardGroupInterface.so5Fixture}
      title={
        <FormattedMessage
          id="play.pro.fixture.enter.seasonality.competition.games.leagueMatchList"
          defaultMessage="{league} Match List"
          values={{
            league: so5LeaderboardGroupInterface.displayName,
          }}
        />
      }
      iconUrl={so5LeaderboardGroupInterface.iconUrl}
      backTo={safePreviousLocation}
      action={
        <Horizontal>
          <FilterItem
            className={classNames({ selected: showGamesWithoutCards })}
            onClick={() => setShowGamesWithoutCards(true)}
          >
            <BodyM as="p" bold>
              <FormattedMessage {...glossary.all} />
            </BodyM>
          </FilterItem>
          <FilterItem
            className={classNames({ selected: !showGamesWithoutCards })}
            onClick={() => setShowGamesWithoutCards(false)}
          >
            <BodyM as="p" bold>
              <FormattedMessage {...navLabels.myCards} />
            </BodyM>
          </FilterItem>
        </Horizontal>
      }
    >
      <Content
        style={
          { '--color': so5LeaderboardGroupInterface.color } as CSSProperties
        }
      >
        <Vertical gap={3}>
          {sortedDays.map(day => (
            <Vertical key={day}>
              <DayContainer center>
                <Chip>
                  <FormattedDate
                    value={day}
                    day="numeric"
                    month="short"
                    weekday="long"
                    timeZone="UTC"
                  />
                </Chip>
              </DayContainer>
              <Vertical gap={isLaptopAndAbove ? 0 : 2}>
                {gamesWithCardsPreviewByDay[day]?.map(
                  ({ game, cards, totalCount }) => {
                    const cardsPreview = (
                      <OverlappingAvatars
                        avatars={cards.map(({ slug, avatarUrl, rarity }) => ({
                          slug,
                          avatarUrl,
                          scarcity: rarity || undefined,
                        }))}
                        additionalPlayersCount={totalCount - cards.length}
                        size={isLaptopAndAbove ? 4 : undefined}
                      />
                    );

                    return isLaptopAndAbove ? (
                      <HorizontalGame
                        key={game.id}
                        game={game}
                        right={cardsPreview}
                        onClick={() => openDialog(DialogKey.game, game.id)}
                      />
                    ) : (
                      <GameWrapper key={game.id}>
                        <Game
                          game={game}
                          topRight={cardsPreview}
                          onClick={() => openDialog(DialogKey.game, game.id)}
                        />
                      </GameWrapper>
                    );
                  }
                )}
              </Vertical>
            </Vertical>
          ))}
          <InfiniteScrollLoader />
        </Vertical>
      </Content>
    </WithFixtureHeaderLayout>
  );
};

CompetitionGamesPage.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment CompetitionGamesPage_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      displayName
      color
      iconUrl
      so5Fixture {
        slug
        type
        ...WithFixtureHeaderLayout_so5Fixture
      }
      so5LeagueTrack(slug: $trck) {
        slug
        entrySo5Leaderboard {
          slug
        }
      }
    }
    ${WithFixtureHeaderLayout.fragments.so5Fixture}
  ` as TypedDocumentNode<CompetitionGamesPage_so5LeaderboardGroupInterface>,
  paginatedSo5LeaderboardGroupInterface: gql`
    fragment CompetitionGamesPages_paginatedSo5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      so5LeagueTrack(slug: $trck) {
        slug
        entrySo5Leaderboard {
          slug
          gamesWithMyCardsPreview(
            sortBy: GAME_DATE
            withCards: $withCards
            after: $cursor
          ) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              id
              cards {
                slug
                rarity: rarityTyped
                avatarUrl: pictureUrl(derivative: "avatar")
              }
              totalCount
              game {
                id
                date
                ...HorizontalGame_anyGameInterface
                ...Game_anyGameInterface
              }
            }
          }
        }
      }
    }
    ${HorizontalGame.fragments.anyGameInterface}
    ${Game.fragments.anyGameInterface}
  ` as TypedDocumentNode<CompetitionGamesPages_paginatedSo5LeaderboardGroupInterface>,
};
